/**
 * @file sheets/bottom-sheet/bottom-sheet-modal.tsx
 *
 * Modal Bottom Sheet — blocks background interaction.
 *
 * Android Jetpack Compose parity:
 * - Entry animation: slides up from y=100% (GAP 6 fix)
 * - PartiallyExpanded default: opens at 50% screen (GAP 1 via isModal flag)
 * - sheetMaxWidth: max-width 640px centered (GAP 2 fix)
 * - Scrim opacity linked to drag position via MotionValue (GAP 8 fix)
 * - DragHandle keyboard-accessible with cycle (GAP 3 fix)
 * - gesturesEnabled prop to disable all drag (GAP 7 fix)
 * - containerColor / contentColor props (GAP 9 fix)
 * - IME / Visual Viewport handled by hook (GAP 11 fix)
 *
 * @see https://m3.material.io/components/sheets-bottom/guidelines#modal
 */

import * as RadixDialog from "@radix-ui/react-dialog";
import { AnimatePresence, m } from "motion/react";
import * as React from "react";
import { cn } from "../../../lib/utils";
import { ScrollArea } from "../../scroll-area";
import { DEFAULT_SPATIAL_SPRING } from "../../shared/motion-tokens";
import { DragHandle } from "../shared/drag-handle";
import { Scrim } from "../shared/scrim";
import type { BaseSheetProps, BottomSheetSnapPoint } from "../shared/types";
import { DEFAULT_SHEET_BG, resolveDividers } from "../shared/utils";
import { useBottomSheet } from "./use-bottom-sheet";

// ─── Animation constants ───────────────────────────────────────────────────────

/**
 * Entry: slide up from bottom (y: 100% → 0) + fade.
 * Exit: slide back down (y: 0 → 100%) + fade.
 * Mirrors Android AnchoredDraggable defaultSpatialSpec motion.
 */
const SHEET_ANIM = {
	initial: { opacity: 0.6 },
	animate: { opacity: 1, transition: DEFAULT_SPATIAL_SPRING },
	exit: {
		opacity: 0,
		transition: { duration: 0.22, ease: "easeIn" as const },
	},
} as const;

// ─── Types ────────────────────────────────────────────────────────────────────

/**
 * Props for the modal Bottom Sheet.
 */
export interface BottomSheetModalProps extends BaseSheetProps {
	/**
	 * Maximum height of the sheet.
	 * @default "90dvh"
	 */
	maxHeight?: string;

	/**
	 * Hide the drag handle visual indicator.
	 * @default false
	 */
	hideHandle?: boolean;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const ABSOLUTE_MAX_HEIGHT =
	"calc(100dvh - max(56px, env(safe-area-inset-top, 0px)))";

function resolveMaxHeight(
	maxHeight: string,
	snapPoints?: BottomSheetSnapPoint[],
): string {
	if (maxHeight === "90dvh" && snapPoints && snapPoints.length > 0)
		return ABSOLUTE_MAX_HEIGHT;
	return maxHeight;
}

/** Converts sheetMaxWidth to a CSS string value. */
function resolveSheetMaxWidth(value?: string | number): string {
	if (value === undefined) return "var(--m3-bottom-sheet-max-width, 640px)";
	if (typeof value === "number") return `${value}px`;
	return value;
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Modal Bottom Sheet that slides up from the bottom edge.
 *
 * Renders into a portal, traps focus, locks body scroll, and blocks
 * background interaction with an animated Scrim.
 *
 * Dismiss by: swiping down, clicking the Scrim, or pressing Escape.
 *
 * @example
 * ```tsx
 * <BottomSheetModal isOpen={isOpen} onClose={() => setIsOpen(false)} aria-labelledby="sheet-title">
 *   <h2 id="sheet-title">Title</h2>
 *   <p>Modal sheet content</p>
 * </BottomSheetModal>
 * ```
 */
export const BottomSheetModal = React.forwardRef<
	HTMLDivElement,
	BottomSheetModalProps
>(
	(
		{
			isOpen,
			onClose,
			children,
			className,
			maxHeight = "90dvh",
			hideHandle = false,
			"aria-labelledby": ariaLabelledBy,
			"aria-label": ariaLabel,
			header,
			footer,
			divider,
			scrollAreaProps,
			snapPoints,
			defaultSnapPoint,
			onSnapChange,
			gesturesEnabled = true,
			containerColor,
			sheetMaxWidth,
		},
		ref,
	) => {
		const {
			scope,
			y,
			scrimOpacity,
			isContentScrollable,
			snapTo,
			cycleSnapPoint,
		} = useBottomSheet({
			isOpen,
			onClose,
			snapPoints,
			defaultSnapPoint,
			onSnapChange,
			gesturesEnabled,
			isModal: true,
			maxHeight: resolveMaxHeight(maxHeight, snapPoints),
		});

		React.useEffect(() => {
			const node = (scope as unknown as { current: HTMLDivElement | null })
				.current;
			if (node) {
				(
					node as HTMLDivElement & {
						snapTo: (sp: BottomSheetSnapPoint) => void;
					}
				).snapTo = snapTo;
			}
		}, [snapTo, scope]);

		const resolvedDividers = React.useMemo(
			() => resolveDividers(divider),
			[divider],
		);

		const resolvedSheetMaxWidth = React.useMemo(
			() => resolveSheetMaxWidth(sheetMaxWidth),
			[sheetMaxWidth],
		);

		const mergedRef = React.useCallback(
			(node: HTMLDivElement | null) => {
				scope(node);
				if (typeof ref === "function") ref(node);
				else if (ref) ref.current = node;
			},
			[scope, ref],
		);

		return (
			<RadixDialog.Root
				open={isOpen}
				onOpenChange={(open) => !open && onClose()}
			>
				<RadixDialog.Portal forceMount>
					<AnimatePresence>
						{isOpen && (
							<RadixDialog.Overlay asChild key="scrim" forceMount>
								<Scrim onClick={onClose} style={{ opacity: scrimOpacity }} />
							</RadixDialog.Overlay>
						)}

						{isOpen && (
							<RadixDialog.Content
								asChild
								key="content"
								forceMount
								aria-describedby={undefined}
								aria-labelledby={ariaLabelledBy}
								aria-label={ariaLabel}
							>
								<m.div
									ref={mergedRef}
									className={cn(
										// Positioning: fixed at bottom, centered with max-width
										"fixed bottom-0 z-50",
										"left-0 right-0 mx-auto",
										// Visual style
										"rounded-t-[28px]",
										!containerColor && DEFAULT_SHEET_BG,
										"flex flex-col overflow-hidden",
										"outline-none focus-visible:ring-2 focus-visible:ring-m3-primary focus-visible:ring-inset",
										className,
									)}
									style={{
										backgroundColor: containerColor,
										height: "var(--sheet-vh, 100dvh)",
										// Container must span the full available viewport height so that
										// snap points above the default maxHeight (e.g. "full", "70%")
										// can reveal their content via the y (translateY) offset mechanism.
										maxHeight: ABSOLUTE_MAX_HEIGHT,
										maxWidth: resolvedSheetMaxWidth,
										y,
									}}
									{...SHEET_ANIM}
								>
									<RadixDialog.Title className="sr-only">
										{ariaLabel || "Bottom Sheet"}
									</RadixDialog.Title>

									{!hideHandle && <DragHandle onCycle={cycleSnapPoint} />}

									{header && <div className="shrink-0 px-6 pb-4">{header}</div>}
									{header && resolvedDividers.header && (
										<hr className="border-t border-m3-outline-variant w-full shrink-0 m-0" />
									)}

									<ScrollArea
										type="hover"
										{...scrollAreaProps}
										className={cn(
											"flex-1 min-h-0 min-w-0",
											scrollAreaProps?.className,
										)}
										viewportClassName={cn(
											"px-6 pb-6 overscroll-contain",
											header ? "pt-4" : "",
											scrollAreaProps?.viewportClassName,
										)}
										viewportProps={{
											...scrollAreaProps?.viewportProps,
											style: {
												...scrollAreaProps?.viewportProps?.style,
												overflowY: isContentScrollable ? "auto" : "hidden",
											},
										}}
									>
										{children}
									</ScrollArea>

									{footer && resolvedDividers.footer && (
										<hr className="border-t border-m3-outline-variant w-full shrink-0 m-0" />
									)}
									{footer && (
										<div className="shrink-0 px-6 pt-4 pb-6">{footer}</div>
									)}
								</m.div>
							</RadixDialog.Content>
						)}
					</AnimatePresence>
				</RadixDialog.Portal>
			</RadixDialog.Root>
		);
	},
);

BottomSheetModal.displayName = "BottomSheetModal";
